import { all, takeLatest, put, call } from 'redux-saga/effects';
import {
  fetchOrganizationPortfolioSummary,
  fetchAccountPortfolioSummary,
  fetchOrganizationRenewalsSummary,
  fetchAccountRenewalsSummary,
  fetchCockpitSuccessPlans,
  fetchCockpitTasks,
  fetchCockpitPlaybooks,
  createActionItemProcessRequest,
  fetchActionItems,
  fetchActionItemsWithId,
  updateActionItemActivity,
} from '../routines/cockpitRoutines';
import { request } from '../../common/util/RestClient';
import envConfig from '../../config';
import SUCCESS_MESSAGES from '../../common/constants/SuccessMessagesConstants';
import { HTTP_NO_CONTENT_FOUND } from '../../common/constants/CommonConstants';
import { GTM_TRACK_EVENT } from '../../common/GoogleTagManager/GoogleTagManager';
import { GTM_EVENTS } from '../../common/GoogleTagManager/GTMEventConstants';

function* fetchCockpitOrganizationSaga({ payload }) {
  const { currencyDefinitionId } = payload;
  try {
    const url = `${envConfig.APIG_URL}core/organizations/cockpit?displayCurrencyId=${currencyDefinitionId}&health=true&globalFieldsToInclude=mrr`;
    const response = yield call(request, url);
    const organizationData = response.data;
    yield put(fetchOrganizationPortfolioSummary.success({ organizationData }));
  } catch (error) {
    yield put(fetchOrganizationPortfolioSummary.failure({ error }));
  }
}

function* fetchCockpitAccountSaga({ payload }) {
  const { currencyDefinitionId } = payload;
  try {
    const url = `${envConfig.APIG_URL}core/accounts/cockpit?displayCurrencyId=${currencyDefinitionId}&health=true&globalFieldsToInclude=mrr`;
    const response = yield call(request, url);
    const accountData = response.data;
    yield put(fetchAccountPortfolioSummary.success({ accountData }));
  } catch (error) {
    yield put(fetchAccountPortfolioSummary.failure({ error }));
  }
}

function* fetchCockpitOrganizationRenewalsSaga({ payload }) {
  const { selectedInterval, currencyDefinitionId } = payload;
  try {
    const url = `${envConfig.APIG_URL}core/organizations/cockpit?displayCurrencyId=${currencyDefinitionId}&filter=${selectedInterval}&globalFieldsToInclude=mrr,renewal_date`;
    const response = yield call(request, url);
    const organizationData = response.data;
    yield put(fetchOrganizationRenewalsSummary.success({ organizationData }));
  } catch (error) {
    yield put(fetchOrganizationRenewalsSummary.failure({ error }));
  }
}

function* fetchCockpitAccountRenewalsSaga({ payload }) {
  const { selectedInterval, currencyDefinitionId } = payload;
  try {
    const url = `${envConfig.APIG_URL}core/accounts/cockpit?displayCurrencyId=${currencyDefinitionId}&filter=${selectedInterval}&globalFieldsToInclude=mrr,renewal_date`;
    const response = yield call(request, url);
    const accountData = response.data;
    yield put(fetchAccountRenewalsSummary.success({ accountData }));
  } catch (error) {
    yield put(fetchAccountRenewalsSummary.failure({ error }));
  }
}

function* fetchCockpitSuccessPlansSaga({ payload }) {
  const { selectedInterval } = payload;
  try {
    const url = `${envConfig.APIG_URL}success-plan/success-plan/cockpit?filter=${selectedInterval}`;
    const response = yield call(request, url);
    const successPlanData = response.data;
    yield put(fetchCockpitSuccessPlans.success({ successPlanData }));
  } catch (error) {
    yield put(fetchCockpitSuccessPlans.failure({ error }));
  }
  GTM_TRACK_EVENT(GTM_EVENTS.NAV_COCKPIT);
}

function* fetchCockpitTasksSaga({ payload }) {
  const { selectedInterval } = payload;

  try {
    const url = `${envConfig.APIG_URL}playbook/tasks/cockpit?filter=${selectedInterval}`;
    const response = yield call(request, url);
    const taskData = response.data;
    yield put(fetchCockpitTasks.success({ taskData }));
  } catch (error) {
    yield put(fetchCockpitTasks.failure({ error }));
  }
}

function* fetchCockpitPlaybooksSaga({ payload }) {
  const { selectedInterval, userId } = payload;

  try {
    const url = `${envConfig.APIG_URL}playbook/playbook-instance/cockpit?filter=${selectedInterval}&userId=${userId}`;
    const response = yield call(request, url);
    const playbookData = response.data;
    yield put(fetchCockpitPlaybooks.success({ playbookData }));
  } catch (error) {
    yield put(fetchCockpitPlaybooks.failure({ error }));
  }
}

export function* fetchActionItemsSaga() {
  try {
    const url = `${envConfig.APIG_URL}core/action-items/process-requests/process-request`;
    const response = yield call(request, url, {}, false, true);
    let actionItemData;
    if (response?.httpStatus === HTTP_NO_CONTENT_FOUND) {
      actionItemData = response?.httpStatus;
    } else {
      actionItemData = response?.data?.data;
    }
    yield put(fetchActionItems.success(actionItemData));
  } catch (error) {
    yield put(fetchActionItems.failure({ error }));
  }
}

export function* createActionItemProcessRequestSaga({ payload }) {
  try {
    const URL = `${envConfig.APIG_URL}core/action-items/process-requests`;
    const response = yield call(request, URL, {
      method: 'POST',
      payload,
    });
    yield put(
      createActionItemProcessRequest.success({
        response,
        payload,
        successMessage: SUCCESS_MESSAGES.CREATE_PROCESS_REQUEST,
      })
    );

    const actionItemPayload = response.data.processRequestId;

    yield put(fetchActionItemsWithId(actionItemPayload));
  } catch (error) {
    yield put(createActionItemProcessRequest.failure({ error }));
  }
}

export function* updateActionItemActivitySaga({ payload }) {
  const { actionItemId, ...rest } = payload;
  try {
    const URL = `${envConfig.APIG_URL}core/action-items/${actionItemId}/activity`;
    const response = yield call(request, URL, {
      method: 'POST',
      payload: { ...rest.payload },
    });
    yield put(updateActionItemActivity.success(response.data));
  } catch (error) {
    yield put(updateActionItemActivity.failure({ error }));
  }
}

export default function* root() {
  yield all([
    takeLatest(
      fetchOrganizationPortfolioSummary.TRIGGER,
      fetchCockpitOrganizationSaga
    ),
    takeLatest(fetchAccountPortfolioSummary.TRIGGER, fetchCockpitAccountSaga),
    takeLatest(
      fetchOrganizationRenewalsSummary.TRIGGER,
      fetchCockpitOrganizationRenewalsSaga
    ),
    takeLatest(
      fetchAccountRenewalsSummary.TRIGGER,
      fetchCockpitAccountRenewalsSaga
    ),
    takeLatest(fetchCockpitSuccessPlans.TRIGGER, fetchCockpitSuccessPlansSaga),
    takeLatest(fetchCockpitTasks.TRIGGER, fetchCockpitTasksSaga),
    takeLatest(fetchCockpitPlaybooks.TRIGGER, fetchCockpitPlaybooksSaga),
    takeLatest(
      createActionItemProcessRequest.TRIGGER,
      createActionItemProcessRequestSaga
    ),
    takeLatest(fetchActionItems.TRIGGER, fetchActionItemsSaga),

    takeLatest(fetchActionItemsWithId, fetchActionItemsSaga),
    takeLatest(updateActionItemActivity.TRIGGER, updateActionItemActivitySaga),
  ]);
}
